import * as manualApiClientConfigs from './config/index.js';
import { getClients } from './get-clients.js';
import BaseApiClient from './base-api-client.js';
import getBaseUrl from './environment.js';
import registerEndpoints from './register.js';

/**
 * Separate the parameters for an endpoint by path and query.
 */
const splitParams = (params, endpoint) => Object.entries(params || {}).reduce((acc, [key, value]) => {
  const pattern = new RegExp(`{${key}}`);
  if (pattern.test(endpoint)) {
    acc.pathParams[key] = value;
    return acc;
  }
  acc.queryParams[key] = value;
  return acc;
}, {
  queryParams: {},
  pathParams: {}
});

/**
 * Slot path parameters into an endpoint.
 */
const populateEndpoint = (endpoint, pathParams) => {
  let populatedEndpoint = endpoint;
  Object.entries(pathParams).forEach(([key, value]) => {
    populatedEndpoint = populatedEndpoint.replace(new RegExp(`{${key}}`), value);
  });
  const remainingMatches = populatedEndpoint.match(/\{.*\}/g);
  if (remainingMatches !== null && remainingMatches !== void 0 && remainingMatches.length) {
    throw new Error(`Missing required path parameter(s): ${remainingMatches.join(', ')}`);
  }
  return populatedEndpoint;
};

/**
 * Make an API request based on the operation config.
 */
const operate = (client, operationConfig, params = {}, data = {}) => {
  const {
    endpoint,
    method
  } = operationConfig;
  const {
    queryParams,
    pathParams
  } = splitParams(params, endpoint);
  const populatedEndpoint = populateEndpoint(endpoint, pathParams);
  const opts = {
    method
  };
  if (Object.keys(data).length) {
    opts.data = data;
  }
  if (Object.keys(queryParams).length) {
    opts.params = queryParams;
  }
  return client.request(populatedEndpoint, opts);
};

/**
 * Create the automatically configured API clients.
 */
const createApiClients = ({
  env,
  getToken,
  refreshToken,
  baseURLs = {}
}) => Object.entries(getClients()).reduce((obj, [apiName, {
  operations,
  config
}]) => {
  const {
    defaultHeaders,
    qsOptions
  } = config || {};
  const baseURL = getBaseUrl(apiName, env, baseURLs);
  const client = new BaseApiClient({
    apiName,
    baseURL,
    getToken,
    refreshToken,
    defaultHeaders,
    qsOptions
  });
  obj[apiName] = Object.entries(operations).reduce((acc, [name, operation]) => ({
    ...acc,
    [name]: (params, data) => operate(client, operation, params, data)
  }), {});
  return obj;
}, {});

/**
 * Create the manually configured API clients.
 */
const createManualApiClients = ({
  env,
  siteKey,
  getToken,
  refreshToken,
  baseURLs = {}
}) => Object.keys(manualApiClientConfigs).reduce((obj, apiName) => {
  const {
    settings,
    endpoints
  } = manualApiClientConfigs[apiName];
  const baseURL = getBaseUrl(apiName, env, baseURLs);
  const client = new BaseApiClient({
    apiName,
    baseURL,
    getToken,
    refreshToken,
    ...settings
  });
  obj[apiName] = Object.keys(endpoints).reduce((entities, entity) => {
    var _endpoints$entity, _endpoints$entity2;
    entities[entity] = registerEndpoints(client, siteKey, endpoints[entity], (_endpoints$entity = endpoints[entity]) === null || _endpoints$entity === void 0 ? void 0 : _endpoints$entity.appendClientToEndpoint, (_endpoints$entity2 = endpoints[entity]) === null || _endpoints$entity2 === void 0 ? void 0 : _endpoints$entity2.appendLanguage);
    return entities;
  }, {});
  return obj;
}, {});

/**
 * Create the fabric api clients.
 */
export default (ctx => ({
  ...createManualApiClients(ctx),
  ...createApiClients(ctx)
}));